import React, { useState } from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import * as authApi from '../authApi';
import PasswordInput from '../components/PasswordInput';
import { isValidEmail, suggestEmailFix } from '../util/email';

interface Props { navigate: (s: Screen) => void; user: User | null; refreshUser: () => Promise<any>; }

export default function Account({ navigate, user, refreshUser }: Props) {
  // Change email
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);

  // Change password
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  // Delete account
  const [showDelete, setShowDelete] = useState(false);
  const [delPw, setDelPw] = useState('');
  const [delConfirm, setDelConfirm] = useState('');
  const [delErr, setDelErr] = useState('');
  const [delBusy, setDelBusy] = useState(false);

  const emailSuggestion = suggestEmailFix(newEmail);

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault(); setEmailErr(''); setEmailMsg('');
    if (!isValidEmail(newEmail)) { setEmailErr('Please enter a valid email address.'); return; }
    if (emailSuggestion) { setEmailErr(`Did you mean ${emailSuggestion}? Fix it or re-enter to continue.`); return; }
    setEmailBusy(true);
    try {
      await authApi.changeEmail(emailPw, newEmail);
      await refreshUser();
      setEmailMsg(`Your email is now ${newEmail.trim().toLowerCase()}.`);
      setNewEmail(''); setEmailPw('');
    } catch (err: any) {
      setEmailErr(err?.message || 'Could not change your email. Please try again.');
    } finally { setEmailBusy(false); }
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault(); setPwErr(''); setPwMsg('');
    if (newPw.length < 8) { setPwErr('Your new password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setPwErr("The new passwords don't match."); return; }
    setPwBusy(true);
    try {
      await authApi.changePassword(curPw, newPw);
      setPwMsg('Your password has been changed.');
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) {
      setPwErr(err?.message || 'Could not change your password. Please try again.');
    } finally { setPwBusy(false); }
  };

  const submitDelete = async (e: React.FormEvent) => {
    e.preventDefault(); setDelErr('');
    if (delConfirm.trim().toUpperCase() !== 'DELETE') { setDelErr('Type DELETE to confirm.'); return; }
    setDelBusy(true);
    try {
      await authApi.deleteAccount(delPw);
      // Account and all data are gone; the session cookie is cleared. Back to the top.
      navigate('landing');
    } catch (err: any) {
      setDelErr(err?.message || 'Could not delete your account. Please try again.');
      setDelBusy(false);
    }
  };

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          Dashboard
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Account settings</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 32 }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Signed in as <strong>{user?.email}</strong></p>

        {/* Change email */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, marginBottom: 4 }}>Change email</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>This is your login and where we send you email. Make sure it's spelled correctly.</p>
          <form onSubmit={submitEmail}>
            <div className="form-group">
              <label>New email</label>
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="you@school.org" />
              {emailSuggestion && <p className="form-hint" style={{ color: '#b87800' }}>Did you mean <strong>{emailSuggestion}</strong>? <span style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer' }} onClick={() => setNewEmail(emailSuggestion)}>Use it</span></p>}
            </div>
            <div className="form-group"><label>Your password</label><PasswordInput value={emailPw} onChange={e => setEmailPw(e.target.value)} placeholder="Confirm it's you" autoComplete="current-password" /></div>
            {emailErr && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{emailErr}</div>}
            {emailMsg && <div style={{ background: 'var(--green-light)', color: 'var(--green)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{emailMsg}</div>}
            <button type="submit" className="btn-primary" disabled={emailBusy || !newEmail || !emailPw}>{emailBusy ? 'Saving…' : 'Update email'}</button>
          </form>
        </div>

        {/* Change password */}
        <div className="card">
          <h2 style={{ fontSize: 18, marginBottom: 16 }}>Change password</h2>
          <form onSubmit={submitPassword}>
            <div className="form-group"><label>Current password</label><PasswordInput value={curPw} onChange={e => setCurPw(e.target.value)} placeholder="Your current password" autoComplete="current-password" /></div>
            <div className="form-group"><label>New password</label><PasswordInput value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" /></div>
            <div className="form-group"><label>Confirm new password</label><PasswordInput value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Type it again" autoComplete="new-password" /></div>
            {pwErr && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{pwErr}</div>}
            {pwMsg && <div style={{ background: 'var(--green-light)', color: 'var(--green)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{pwMsg}</div>}
            <button type="submit" className="btn-primary" disabled={pwBusy || !curPw || !newPw || !confirmPw}>{pwBusy ? 'Saving…' : 'Update password'}</button>
          </form>
        </div>

        {/* Danger zone: delete account and all data */}
        <div className="card" style={{ marginTop: 20, border: '1px solid var(--coral)' }}>
          <h2 style={{ fontSize: 18, marginBottom: 4, color: 'var(--coral)' }}>Delete account</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
            Permanently delete your account and <strong>all of your data</strong> — your students, conversations, strategies, ratings, check-ins, and debriefs. If you have an active subscription, it's cancelled automatically. This cannot be undone.
          </p>
          {!showDelete ? (
            <button type="button" onClick={() => setShowDelete(true)} style={{ background: 'transparent', color: 'var(--coral)', border: '2px solid var(--coral)', borderRadius: 'var(--radius)', padding: '12px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
              Delete my account…
            </button>
          ) : (
            <form onSubmit={submitDelete}>
              <div className="form-group"><label>Your password</label><PasswordInput value={delPw} onChange={e => setDelPw(e.target.value)} placeholder="Confirm it's you" autoComplete="current-password" /></div>
              <div className="form-group"><label>Type DELETE to confirm</label><input type="text" value={delConfirm} onChange={e => setDelConfirm(e.target.value)} placeholder="DELETE" autoComplete="off" /></div>
              {delErr && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{delErr}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => { setShowDelete(false); setDelPw(''); setDelConfirm(''); setDelErr(''); }} className="btn-secondary" style={{ flex: 1 }} disabled={delBusy}>Cancel</button>
                <button type="submit" style={{ flex: 1, background: 'var(--coral)', color: 'white', border: 'none', borderRadius: 'var(--radius)', padding: '14px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: (delBusy || !delPw || !delConfirm) ? 0.6 : 1 }} disabled={delBusy || !delPw || !delConfirm}>
                  {delBusy ? 'Deleting…' : 'Permanently delete'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
